import { Injectable } from '@angular/core';
import { MessageService } from 'primeng/api';
import {
  HttpRequest,
  HttpHandler,
  HttpEvent,
  HttpInterceptor,
  HttpErrorResponse,
} from '@angular/common/http';
import { Observable, throwError } from 'rxjs';
import { catchError } from 'rxjs/operators';
import { environment } from '../../environments/environment';
import { isSuppressedApiError } from '../_constants/suppressedApiErrors';

/** Toast key of the global <p-toast> in app.component.html. */
const API_TOAST_KEY = 'apiValidation';

@Injectable()
export class ErrorInterceptor implements HttpInterceptor {
  constructor(private messageService: MessageService) { }

  intercept(
    request: HttpRequest<any>,
    next: HttpHandler,
  ): Observable<HttpEvent<any>> {
    if (this.isIdentityProviderTokenEndpoint(request.url)) {
      // Identity-provider token endpoint: the OAuth error shape is {error, error_description},
      // not the shape the rest of the estate returns, so it keeps its own unwrapping. The
      // rethrown value stays a string here because existing callers depend on that.
      return next.handle(request).pipe(catchError(error => {
        const oauthCode = error?.error?.error ?? error?.error;
        const oauthDescription = error?.error?.error_description;

        if (error.error) {
          error = error.error;
        }
        if (error.error_description) {
          error = error.error_description;
        } else if (error.error) {
            if(error.error!=='invalid_grant'){
          this.messageService.add({
            key: 't2',
            severity: 'error',
            summary: ' Error',
            detail: 'Username required',
        });
    }

        }

        // Previously this only called a no-op notifier, so a failed token exchange showed
        // the user nothing at all.
        if (oauthCode !== 'invalid_grant') {
          const detail =
            (typeof oauthDescription === 'string' && oauthDescription.trim()) ||
            (typeof error === 'string' && error.trim()) ||
            'Sign-in failed. Please try again.';
          this.messageService.add({
            key: API_TOAST_KEY,
            severity: 'error',
            detail,
          });
        }

        return throwError(error);
      }));
    }
    // The token-refresh call is plumbing, not a user action. When it fails the session has
    // expired and TokenService logs the user out - surfacing the server's "Invalid refresh
    // token" on the way out only alarms them.
    const isTokenRefresh = request.url === environment.authServerRefreshApi;

    return next.handle(request).pipe(
      catchError((err: HttpErrorResponse) => {
        if (err.status === 401 || isTokenRefresh) {
          // TokenInterceptor handles refresh and retry, so no toast here - but still give
          // the error a readable form for any component that renders it.
          return throwError(() => this.withDisplayMessage(err, 'Your session has expired. Please sign in again.'));
        }
        // Endpoints on the suppression list are known to answer "nothing here" with an error
        // status. The error is still rethrown untouched below, so component-level handling is
        // unaffected - only the toast is skipped. See _constants/suppressedApiErrors.ts.
        const suppressToast = isSuppressedApiError(request.url, err.status);
        if (suppressToast && !environment.production) {
          console.debug(`[ErrorInterceptor] toast suppressed for ${err.status} ${request.url}`);
        }

        // Parse the body once - the APIs in this estate return several different shapes.
        const body = this.parseErrorBody(err);

        // Field-level validation errors, wherever they came from.
        const fieldErrors = this.extractFieldErrors(body);
        if (fieldErrors.length) {
          // Joined rather than newline-separated: PrimeNG escapes the detail, so a "\n"
          // would render as whitespace on a single line anyway.
          const detail = fieldErrors.join('  •  ');
          if (!suppressToast) {
            this.messageService.add({
              key: API_TOAST_KEY,
              severity: 'error',
              detail,
              // 4s matches the app's convention. Toast close buttons are hidden globally, so
              // a long-lived toast cannot be dismissed and just sits in the user's way.
              life: 4000,
            });
          }
          return throwError(() => this.withDisplayMessage(err, detail));
        }

        const message = this.extractMessage(err, body);

        if (!suppressToast) {
          this.messageService.add({
            key: API_TOAST_KEY,
            severity: 'error',
            detail: message,
          });
        }

        // Rethrow the response itself. Previously this rethrew a plain string, which meant
        // no caller could inspect status or the validation payload.
        return throwError(() => this.withDisplayMessage(err, message));
      }),
    );
  }

  /**
   * True only for the identity provider's OAuth token-exchange endpoint
   * (https://login.microsoftonline.com/&lt;tenant&gt;/oauth2/v2.0/token).
   *
   * Matched on the path's final segment rather than with url.includes('token'), which also
   * caught application endpoints that merely contain the word - most importantly
   * `api/user/Account/checktokenexistnotexpired/?token=...` in the password-reset flow, which
   * would then be parsed with OAuth's {error, error_description} shape instead of ours.
   */
  private isIdentityProviderTokenEndpoint(url: string): boolean {
    try {
      const path = new URL(url, window.location.origin).pathname;
      return /\/oauth2\/v2\.0\/token\/?$/i.test(path);
    } catch {
      return false;
    }
  }

  /**
   * Attaches the human-readable message to the error before rethrowing.
   *
   * This interceptor used to rethrow a bare string, so callers that render the caught value
   * (`this.er = error` etc.) happened to display text. Rethrowing the response keeps the
   * status and validation payload available, and `displayMessage` gives those callers
   * something printable without each of them re-deriving it.
   */
  private withDisplayMessage(err: HttpErrorResponse, message: string): HttpErrorResponse {
    const target = err as any;
    target.displayMessage = message;

    // Around 40 templates render the caught error directly - `<p-toast><P>{{this.error}}</P>`
    // - and several components pass it straight into a toast's detail. Those worked only
    // because this interceptor used to rethrow a bare string. Rethrowing the response is
    // what makes status and the validation payload available, so instead make the object
    // stringify to the same message: the legacy templates keep working untouched.
    target.toString = () => message;

    return err;
  }

  /**
   * Normalises the error body, which arrives in several shapes across these APIs:
   *
   *  - an already-parsed object (the usual JSON response)
   *  - a raw JSON string, when the caller asked for responseType 'text' - the sensor bulk
   *    endpoints do this, so their validation payload would otherwise never be inspected
   *  - a double-encoded string, from `BadRequest(JsonConvert.SerializeObject(new { message }))`
   *    where the body is a JSON string that itself contains JSON
   *  - a plain text sentence, from `BadRequest("No sensor data provided.")`
   *  - a Blob, on file-download endpoints, which cannot be read synchronously
   *
   * Returns an object, a string, or null when nothing usable is present.
   */
  private parseErrorBody(err: HttpErrorResponse): any {
    const body = err.error;

    if (body === null || body === undefined) {
      return null;
    }

    // Blobs and browser-level failures carry nothing printable.
    if (typeof Blob !== 'undefined' && body instanceof Blob) {
      return null;
    }
    if (typeof ErrorEvent !== 'undefined' && body instanceof ErrorEvent) {
      return null;
    }
    if (typeof ProgressEvent !== 'undefined' && body instanceof ProgressEvent) {
      return null;
    }

    if (typeof body !== 'string') {
      return body;
    }

    const trimmed = body.trim();
    if (!trimmed) {
      return null;
    }

    if (!/^[{["]/.test(trimmed)) {
      return trimmed; // plain sentence
    }

    try {
      const parsed = JSON.parse(trimmed);
      // Unwrap one level of double-encoding.
      if (typeof parsed === 'string') {
        const inner = parsed.trim();
        if (/^[{[]/.test(inner)) {
          try {
            return JSON.parse(inner);
          } catch {
            return inner;
          }
        }
        return inner;
      }
      return parsed;
    } catch {
      return trimmed;
    }
  }

  /**
   * Flattens a ValidationProblemDetails-style body into readable lines.
   * Shape: { title, status, errors: { "field": ["message", ...] } }
   * Not restricted to 400 - 422 carries the same payload.
   */
  private extractFieldErrors(body: any): string[] {
    const errors = body?.errors ?? body?.Errors;
    if (!errors || typeof errors !== 'object') {
      return [];
    }

    // Defensive: some handlers return errors as a flat array rather than a map.
    if (Array.isArray(errors)) {
      return errors.map(e => String(e)).filter(Boolean);
    }

    const lines: string[] = [];
    for (const [field, messages] of Object.entries<any>(errors)) {
      const label = this.humanizeFieldName(field);
      const text = Array.isArray(messages) ? messages.join(' ') : String(messages);
      lines.push(label ? `${label}: ${text}` : text);
    }
    return lines;
  }

  /**
   * Best available human-readable message, in priority order.
   *
   * The header comes first because ~400 endpoints across master-api and user-api set an
   * explicit `Message` header alongside an empty body - that is the message they intend the
   * user to see, and it beats a generic body title such as
   * "One or more validation errors occurred."
   */
  private extractMessage(err: HttpErrorResponse, body: any): string {
    const header = err.headers?.get('message') || err.headers?.get('Message');
    if (header?.trim()) {
      return header.trim();
    }

    // Never surface a 5xx body: it can carry raw exception text or a stack trace, which is
    // information disclosure and means nothing to the user anyway.
    if (err.status >= 500) {
      return this.defaultMessageForStatus(err);
    }

    if (typeof body === 'string' && body.trim()) {
      return body.trim();
    }

    const candidate =
      body?.message ?? body?.Message ??
      body?.detail ?? body?.Detail ??
      body?.error_description ??
      body?.title ?? body?.Title ??
      (typeof body?.error === 'string' ? body.error : null);

    if (typeof candidate === 'string' && candidate.trim()) {
      return candidate.trim();
    }

    return this.defaultMessageForStatus(err);
  }

  /** Fallbacks for responses that carry no usable body or header. */
  private defaultMessageForStatus(err: HttpErrorResponse): string {
    switch (err.status) {
      case 0:   return 'Unable to reach the server. Check your connection and try again.';
      case 403: return 'You do not have permission to perform this action.';
      case 404: return 'The requested item could not be found.';
      case 409: return 'This conflicts with existing data.';
      case 413: return 'The file or request is too large.';
      case 429: return 'Too many requests. Please wait and try again.';
    }

    if (err.status >= 500) {
      return 'Something went wrong on the server. Please try again.';
    }

    return err.statusText?.trim() || 'Request failed';
  }

  /**
   * "request.SensorName" / "requests[3].ModuleSpName" -> "Sensor Name" / "Module Sp Name (row 4)".
   * Model-state keys carry the binder's parameter path, which means nothing to a user.
   */
  private humanizeFieldName(field: string): string {
    if (!field || field === '$' || field.startsWith('$.')) {
      return '';
    }

    const rowMatch = field.match(/\[(\d+)\]/);
    const rowSuffix = rowMatch ? ` (row ${Number(rowMatch[1]) + 1})` : '';

    const leaf = field.replace(/\[\d+\]/g, '').split('.').pop() ?? field;
    const spaced = leaf
      .replace(/_/g, ' ')
      .replace(/([a-z0-9])([A-Z])/g, '$1 $2')
      .trim();

    if (!spaced) {
      return '';
    }

    return spaced.charAt(0).toUpperCase() + spaced.slice(1) + rowSuffix;
  }
}
